import { useEffect, useRef, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  FocusLayout,
  FocusLayoutContainer,
  useConnectionState,
  useLayoutContext,
  usePinnedTracks,
  useRemoteParticipants,
  useTracks,
} from '@livekit/components-react'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { ConnectionState, Track } from 'livekit-client'
import { Button } from '@/custolink-newdesign/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/custolink-newdesign/ui/dropdown-menu'
import { ParticipantThumbnails } from './ParticipantThumbnails'
import { pickDefaultStageTrack, sameTrack, trackKey, type StageRole } from './stageSelection'

function participantLabel(t: TrackReferenceOrPlaceholder): string {
  return t.participant.name || t.participant.identity
}

/**
 * Selects and pins the equipment-stage track (D-03). Pin is explicit app state, driven only by a
 * user action (StageSourceMenu here, or useFocusToggle in ParticipantThumbnails) - nothing here
 * ever reacts to who is speaking.
 */
export function useStageSelection(role: StageRole) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  )
  const layoutContext = useLayoutContext()
  const pinned = usePinnedTracks(layoutContext)[0]
  // A pin whose participant has since left falls back to the default without dispatching anything.
  const pinnedLive = pinned != null ? tracks.find((t) => sameTrack(t, pinned)) : undefined
  const stageTrack = pinnedLive ?? pickDefaultStageTrack(tracks, role)
  const otherTracks = tracks.filter((t) => !sameTrack(t, stageTrack))

  const pinStage = (track: TrackReferenceOrPlaceholder) => {
    layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: track })
  }
  const clearPin = () => {
    layoutContext.pin.dispatch?.({ msg: 'clear_pin' })
  }

  return { tracks, stageTrack, otherTracks, pinStage, clearPin }
}

/** Explicit source switching, reachable even when thumbnails are collapsed (D-03). */
export function StageSourceMenu(p: {
  tracks: TrackReferenceOrPlaceholder[]
  current?: TrackReferenceOrPlaceholder
  onSelect: (t: TrackReferenceOrPlaceholder) => void
}) {
  const currentKey = p.current != null ? trackKey(p.current) : undefined
  const currentLabel = p.current != null ? participantLabel(p.current) : 'Nobody'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={`On stage: ${currentLabel}. Change source`}
          className="cl:h-auto cl:min-h-[44px] cl:pointer-fine:min-h-9 cl:max-w-[min(60vw,320px)] cl:gap-1.5 cl:rounded-full cl:bg-black/60 cl:pl-3.5 cl:pr-3 cl:text-xs cl:text-white cl:border-white/15 cl:hover:bg-black/75 cl:hover:text-white"
        >
          <span className="cl:text-white/60">On stage</span>
          <span className="cl:truncate cl:font-semibold">{currentLabel}</span>
          <ChevronDown className="cl:size-3.5 cl:shrink-0 cl:text-white/70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Change source</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={currentKey}
          onValueChange={(value) => {
            const next = p.tracks.find((t) => trackKey(t) === value)
            if (next != null) p.onSelect(next)
          }}
        >
          {p.tracks.map((t) => (
            <DropdownMenuRadioItem key={trackKey(t)} value={trackKey(t)}>
              {participantLabel(t)} - {t.source === Track.Source.ScreenShare ? 'screen' : 'camera'}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The pinned equipment stage (D-03): a FocusLayoutContainer with the collapsible thumbnails as
 * the side component and the pinned/default track as the main one, plus a connection-state
 * overlay. Must be mounted inside LiveKitRoom and a LayoutContextProvider.
 */
export function EquipmentStage(p: {
  role: StageRole
  layout: 'desktop' | 'mobile'
  waitingLabel: string
  thumbnailsCollapsed: boolean
  onToggleThumbnails: () => void
  /** Renders Phase 5's sync overlay for this stage's track (RS-09..RS-15); undefined outside a RoomSyncProvider. */
  renderStageOverlay?: (stageTrack: TrackReferenceOrPlaceholder | undefined) => ReactNode
  /** Fires when this viewer explicitly changed the stage here (StageSourceMenu, thumbnails' "Show on stage"). */
  onUserSelectedStage?: (track: TrackReferenceOrPlaceholder) => void
  /** The session's shared active source (Phase 5 D-03): this stage follows it without ever reacting to speaking. */
  followTrackSid?: string | null
}) {
  const { tracks, stageTrack, otherTracks, pinStage } = useStageSelection(p.role)
  const connectionState = useConnectionState()
  const remoteParticipants = useRemoteParticipants()

  // Marks a stage change this component itself caused by following followTrackSid, so the
  // onUserSelectedStage effect below can tell it apart from a change this viewer made here
  // (Phase 4 D-03: the pin stays explicit app state either way - nothing here reacts to speaking).
  const followingKeyRef = useRef<string | null>(null)
  const previousStageKeyRef = useRef<string | undefined>(undefined)
  // The last shared active source we actually applied. Once applied, track-list churn must not
  // re-apply it: that would clobber a stage this viewer explicitly pinned (e.g. a customer's
  // "Show on stage"). A genuinely new followTrackSid is applied again.
  const appliedFollowSidRef = useRef<string | null>(null)
  const stageKey = stageTrack != null ? trackKey(stageTrack) : undefined

  useEffect(() => {
    const previousKey = previousStageKeyRef.current
    previousStageKeyRef.current = stageKey
    if (stageKey === previousKey || stageKey == null || stageTrack == null) {
      return
    }
    if (stageKey === followingKeyRef.current) {
      return
    }
    p.onUserSelectedStage?.(stageTrack)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageKey])

  // Follows the session's shared active source (Phase 5 D-03) by calling the same explicit
  // pinStage this file already uses for StageSourceMenu - never a raw dispatch, and never driven
  // by who is speaking. Keyed on followTrackSid and the list of track sids rather than the tracks
  // array reference, since useTracks can hand back a new array on every render.
  const trackSidsSignature = tracks.map((t) => t.publication?.trackSid ?? '').join('|')
  useEffect(() => {
    if (p.followTrackSid == null) {
      appliedFollowSidRef.current = null
      return
    }
    // Already followed this source: the track list changing underneath (someone toggling a
    // camera, a participant joining) must not re-pin and undo a local choice.
    if (appliedFollowSidRef.current === p.followTrackSid) {
      return
    }
    if (stageTrack?.publication?.trackSid === p.followTrackSid) {
      appliedFollowSidRef.current = p.followTrackSid
      return
    }
    const target = tracks.find((t) => t.publication?.trackSid === p.followTrackSid)
    if (target == null) {
      // Not published yet - leave applied null so this retries when the track list changes.
      return
    }
    followingKeyRef.current = trackKey(target)
    pinStage(target)
    appliedFollowSidRef.current = p.followTrackSid
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.followTrackSid, trackSidsSignature])

  const connecting = connectionState === ConnectionState.Connecting
  const reconnecting =
    connectionState === ConnectionState.Reconnecting ||
    connectionState === ConnectionState.SignalReconnecting
  const waitingForRemote =
    connectionState === ConnectionState.Connected && remoteParticipants.length === 0

  return (
    <FocusLayoutContainer
      className="rs-focus-container"
      data-rs-layout={p.layout}
      data-rs-thumbs-collapsed={String(p.thumbnailsCollapsed)}
    >
      <ParticipantThumbnails
        tracks={otherTracks}
        collapsed={p.thumbnailsCollapsed}
        orientation="vertical"
        compact={p.layout === 'mobile'}
        onToggleCollapsed={p.onToggleThumbnails}
      />

      <div className="rs-stage cl:relative">
        {stageTrack != null ? (
          <FocusLayout trackRef={stageTrack} />
        ) : (
          <div className="cl:absolute cl:inset-0 cl:flex cl:items-center cl:justify-center cl:bg-black cl:text-white/60 cl:text-sm">
            No video yet
          </div>
        )}

        {p.renderStageOverlay?.(stageTrack)}

        <div className="cl:absolute cl:top-2 cl:left-2 cl:z-10">
          <StageSourceMenu tracks={tracks} current={stageTrack} onSelect={pinStage} />
        </div>

        {reconnecting && (
          <div
            role="status"
            className="cl:absolute cl:top-2 cl:left-1/2 cl:-translate-x-1/2 cl:z-10 cl:rounded-full cl:bg-black/75 cl:px-3 cl:py-1.5 cl:text-xs cl:text-white"
          >
            Connection lost - reconnecting...
          </div>
        )}

        {connecting && (
          <div
            role="status"
            className="cl:absolute cl:inset-0 cl:z-10 cl:flex cl:items-center cl:justify-center cl:bg-black/70 cl:text-white cl:text-sm"
          >
            Connecting...
          </div>
        )}

        {!connecting && !reconnecting && waitingForRemote && (
          <div
            role="status"
            className="cl:absolute cl:inset-0 cl:z-10 cl:flex cl:items-center cl:justify-center cl:bg-black/70 cl:text-white cl:text-sm cl:text-center cl:px-4"
          >
            {p.role === 'worker'
              ? `Waiting for ${p.waitingLabel} to join`
              : 'Connecting you to support. Please stay on this screen.'}
          </div>
        )}
      </div>
    </FocusLayoutContainer>
  )
}
